(() => {
  if (window.__mustflorReviewEditorialLoaded) return;
  window.__mustflorReviewEditorialLoaded = true;

  let loadStarted = false;
  let completePool;

  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();

  const productInfo = review => {
    const product = review?.corresponding_product;
    if (!review?.id || !product?.product_url) return null;

    try {
      const url = new URL(product.product_url, location.origin);
      if (url.origin !== location.origin || !/^\/products\/[^/?#]+\/?$/.test(url.pathname)) return null;
      url.hash = '';
      url.search = '';

      let image = '';
      if (product.product_image) {
        try {
          const parsedImage = new URL(product.product_image, location.origin);
          if (/^https?:$/.test(parsedImage.protocol)) image = parsedImage.href;
        } catch (_) {}
      }

      return {
        href: url.pathname,
        image,
        name: clean(product.product_name)
      };
    } catch (_) {
      return null;
    }
  };

  const shuffle = pool => {
    const result = pool.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const normalizeReview = review => {
    const id = String(review?.id || '');
    const product = productInfo(review);
    const text = clean(review?.content);
    if (!id || !product || !text) return null;

    const ratingValue = Number(review.star);
    const stars = Number.isFinite(ratingValue) ? Math.max(1, Math.min(5, ratingValue)) : 5;
    const name = clean(review.author);
    const initials = Array.from(name.replace(/[\s.。·・_\-]/g, '')).slice(0, 2).join('') || 'M';
    const date = clean(review.commented_at).split(' ')[0].replace(/-/g, '/');

    return {
      id,
      product,
      text,
      initials,
      date,
      stars
    };
  };

  const buildCard = review => {
    const card = element('a', 'mustflor-review-editorial__card');
    card.href = review.product.href;
    card.dataset.reviewId = review.id;
    card.dataset.rating = String(review.stars);
    card.setAttribute('aria-label', `${review.initials}的評價；查看${review.product.name || '商品'}`);

    const header = element('div', 'mustflor-review-editorial__header');
    header.append(element('span', 'mustflor-review-editorial__initials', review.initials));

    const rating = element('span', 'mustflor-review-editorial__rating rating');
    rating.setAttribute('role', 'img');
    rating.setAttribute('aria-label', `${review.stars} 顆星`);
    const stars = element('span', 'rating-star');
    stars.style.setProperty('--rating', String(review.stars));
    stars.style.setProperty('--rating-max', '5');
    stars.style.setProperty('--rating-decimal', '0');
    stars.setAttribute('aria-hidden', 'true');
    rating.append(stars);
    header.append(rating);
    card.append(header);

    card.append(element('p', 'mustflor-review-editorial__text', review.text));

    const footer = element('div', 'mustflor-review-editorial__footer');
    footer.append(element('span', 'mustflor-review-editorial__date', review.date));

    if (review.product.image) {
      const image = element('img', 'mustflor-review-editorial__product');
      image.src = review.product.image;
      image.alt = review.product.name;
      image.width = 38;
      image.height = 38;
      image.loading = 'lazy';
      image.addEventListener('error', () => image.remove(), { once: true });
      footer.append(image);
    }

    card.append(footer);
    return card;
  };

  const setupNavigation = (root, track) => {
    if (!root || !track) return;

    const prev = root.querySelector('.mustflor-review-editorial__arrow--prev');
    const next = root.querySelector('.mustflor-review-editorial__arrow--next');
    const pagination = root.querySelector('.mustflor-review-editorial__pagination');

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const getStep = () => {
      const firstCard = track.querySelector('.mustflor-review-editorial__card');
      if (!firstCard) return Math.max(240, track.clientWidth * 0.75);
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return firstCard.getBoundingClientRect().width + gap;
    };

    const rebuildDots = () => {
      if (!pagination) return [];
      pagination.innerHTML = '';
      const dots = [];
      const cardCount = track.querySelectorAll('.mustflor-review-editorial__card').length;
      const dotCount = Math.min(5, Math.max(2, cardCount));

      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mustflor-review-editorial__dot';
        dot.setAttribute('aria-label', `移到評論位置 ${i + 1}`);
        dot.addEventListener('click', () => {
          const max = getMaxScroll();
          track.scrollTo({ left: dotCount === 1 ? 0 : max * (i / (dotCount - 1)), behavior: 'smooth' });
        });
        pagination.appendChild(dot);
        dots.push(dot);
      }
      root.__mustflorReviewDots = dots;
      return dots;
    };

    const update = () => {
      const dots = root.__mustflorReviewDots || [];
      const max = getMaxScroll();
      const hasOverflow = max > 4;
      if (prev) {
        prev.hidden = !hasOverflow;
        prev.disabled = track.scrollLeft <= 3;
      }
      if (next) {
        next.hidden = !hasOverflow;
        next.disabled = track.scrollLeft >= max - 3;
      }
      if (pagination) pagination.hidden = !hasOverflow;
      if (!dots.length) return;
      const ratio = max ? Math.max(0, Math.min(1, track.scrollLeft / max)) : 0;
      const active = Math.round(ratio * (dots.length - 1));
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    };

    if (root.dataset.navigationReady !== 'true') {
      root.dataset.navigationReady = 'true';
      prev?.addEventListener('click', () => track.scrollBy({ left: -getStep(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: getStep(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }

    rebuildDots();
    requestAnimationFrame(update);
  };

  const renderComplete = () => {
    const root = document.querySelector('.mustflor-review-editorial');
    const track = root?.querySelector('.mustflor-review-editorial__track');
    if (!root || !track || !completePool || track.dataset.complete === 'true') return;
    const fragment = document.createDocumentFragment();
    completePool.forEach(review => fragment.append(buildCard(review)));
    track.replaceChildren(fragment);
    track.dataset.poolSize = String(completePool.length);
    track.dataset.complete = 'true';
    track.removeAttribute('aria-busy');
    setupNavigation(root, track);
  };

  const requestPage = async (endpoint, page) => {
    const url = new URL(endpoint);
    url.searchParams.set('page', String(page));
    let lastError;
    for (let attempt = 0; attempt < 3; attempt++) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url.href, { credentials: 'omit', signal: controller.signal });
        if (!response.ok) throw new Error(`Trustoo HTTP ${response.status}`);
        const result = await response.json();
        if (result.code !== 0 || !Array.isArray(result.data?.list) || !result.data?.page) {
          throw new Error('Trustoo invalid page');
        }
        return result.data;
      } catch (error) {
        lastError = error;
        if (attempt < 2) await new Promise(resolve => window.setTimeout(resolve, 500 * (attempt + 1)));
      } finally {
        window.clearTimeout(timeout);
      }
    }
    throw lastError;
  };

  const loadFullReviews = async () => {
    const root = document.querySelector('.mustflor-review-editorial');
    if (!root || loadStarted) return;
    loadStarted = true;
    const track = root.querySelector('.mustflor-review-editorial__track');
    track?.setAttribute('aria-busy', 'true');
    root.dataset.reviewLoadState = 'loading';
    try {
      const shopId = document.querySelector('script[data-name="trustooCarousel"]')?.dataset.shop_id
        || window.ShopifyAnalytics?.meta?.shopId;
      if (!/^\d+$/.test(String(shopId || ''))) throw new Error('Trustoo missing public shop ID');
      const endpoint = new URL('https://api.trustoo.io/api/v1/reviews/get_product_reviews');
      endpoint.search = new URLSearchParams({
        shop_id: String(shopId), limit: '100', no_empty: '2', scene: '1',
        review_filter_type: '2', sort_by: 'commented-at-descending'
      }).toString();
      const reviews = new Map();
      let expectedCount;
      let totalPages;
      for (let page = 1; page === 1 || page <= totalPages; page++) {
        const data = await requestPage(endpoint, page);
        const count = Number(data.page.count);
        const pages = Number(data.page.total_page);
        if (!Number.isInteger(count) || count < 0 || !Number.isInteger(pages) || pages < 0
          || Number(data.page.cur_page) !== page || (count > 0 && pages < 1)) {
          throw new Error('Trustoo inconsistent pagination');
        }
        if (page === 1) {
          expectedCount = count;
          totalPages = pages;
        } else if (count !== expectedCount || pages !== totalPages) {
          throw new Error('Trustoo review pool changed during collection');
        }
        const before = reviews.size;
        data.list.forEach(review => {
          if (review.id != null) reviews.set(String(review.id), review);
        });
        root.dataset.reviewPagesLoaded = String(page);
        root.dataset.reviewPagesTotal = String(totalPages);
        root.dataset.reviewRawTotal = String(expectedCount);
        if (expectedCount > 0 && reviews.size === before) throw new Error('Trustoo repeated or empty page');
      }
      if (reviews.size !== expectedCount) throw new Error('Trustoo incomplete review pool');

      let excludedEmptyText = 0;
      let unlinked = 0;
      const pool = [];
      reviews.forEach(raw => {
        if (!clean(raw?.content)) {
          excludedEmptyText++;
          return;
        }
        const review = normalizeReview(raw);
        if (review) pool.push(review);
        else unlinked++;
      });

      root.dataset.reviewRawUnique = String(reviews.size);
      root.dataset.reviewExcludedRush = '0';
      root.dataset.reviewExcludedEmptyText = String(excludedEmptyText);
      root.dataset.reviewUnlinked = String(unlinked);
      root.dataset.reviewRetained = String(pool.length);
      root.dataset.reviewEmptyText = '0';
      root.dataset.reviewSource = endpoint.pathname;
      completePool = shuffle(pool);
      root.dataset.reviewLoadState = 'complete';
      renderComplete();
    } catch (error) {
      root.dataset.reviewLoadState = 'error';
      track?.removeAttribute('aria-busy');
      console.error('[Mustflor reviews] Full collection failed; no partial pool rendered.', error);
    }
  };

  const afterExecute = window.trustooAfterExecute;
  window.trustooAfterExecute = function (state, options) {
    try {
      const root = document.querySelector('.mustflor-review-editorial');
      const page = state.pageInfo?.reviews;
      if (root && page && state.isMixReview) {
        root.dataset.trustooWidgetTotal = String(page.count);
        root.dataset.trustooWidgetPages = String(page.total);
        root.dataset.trustooWidgetPage = String(page.curPage);
      }
    } finally {
      if (typeof afterExecute === 'function') afterExecute.apply(this, arguments);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadFullReviews, { once: true });
  else loadFullReviews();
  document.addEventListener('shopify:section:load', renderComplete);
})();
